/**
 * dev-feature-accordion — single-open behaviour for the accordion mode of
 * blocks/dev-feature-row.liquid (sections/dev-feature-showcase.liquid).
 *
 * WHY THERE IS BARELY ANY CODE HERE.
 * The rows are real <details>/<summary>. Opening, closing, keyboard operation,
 * the expanded state in the accessibility tree and Find-in-page all come from
 * the browser, so the page works with this file blocked or JavaScript off — the
 * only thing lost is "closing one row when another opens", which is a
 * preference, not a feature. That is the whole reason the spec chose <details>
 * over the button + aria-expanded pattern in assets/dev-faq-item.js.
 *
 * WHY NOT name="" (the platform's own single-open mechanism).
 * `<details name="group">` is the one-line version of this file, and it is not
 * usable yet: Safari only shipped it in 17.4 and the theme still supports older
 * iOS. Worse, its failure mode is silent — on an unsupporting browser every row
 * simply opens independently, which is exactly what this file is for. Revisit
 * when the support floor moves.
 *
 * LOADED AS A MODULE, and the tag is emitted from inside the block, so a page
 * with five rows emits it five times. A module is fetched once and evaluated
 * once per URL however many tags point at it; a classic script would re-run per
 * tag. The window guard below covers the theme editor re-rendering a section
 * into a document that has already run this.
 *
 * DELEGATED FROM `document`, IN THE CAPTURE PHASE. `toggle` does not bubble, so
 * a listener on document only ever sees it with `capture: true`. Delegating
 * means rows the editor adds later work with no rescan.
 *
 * "GROUP" IS RESOLVED BY WALKING UP, not by hardcoding a container class — the
 * same approach and the same reason as assets/dev-faq-item.js. The block is
 * shared and deliberately knows nothing about its consumer, and Shopify may wrap
 * each block in an element of its own, which would defeat a plain
 * `parentElement` lookup by making every row an only child.
 */
if (!window.devFeatureAccordion) {
  window.devFeatureAccordion = true;

  const ITEM = 'details[data-dev-feature-row]';
  /** Set by the section when "only one row open at a time" is switched off. */
  const MULTI = 'data-dev-feature-row-multi';

  /** Nearest ancestor holding more than one row — stops well short of <body>. */
  const groupOf = (item) => {
    let node = item.parentElement;
    while (node && node !== document.body) {
      if (node.querySelectorAll(ITEM).length > 1) return node;
      node = node.parentElement;
    }
    return null;
  };

  /** Closes every other open row in the item's group. */
  const closeOthers = (item) => {
    if (item.hasAttribute(MULTI)) return;
    const group = groupOf(item);
    if (!group) return;
    for (const other of group.querySelectorAll(ITEM)) {
      // Assigning `open` fires another `toggle` on that row, which the listener
      // below handles: it syncs the class and the ARIA state and then returns
      // at `!item.open`. No recursion.
      if (other !== item && other.open) other.open = false;
    }
  };

  /*
   * NO JOLT (client video 2026-10-20, Track "More Ways": "why does it do this
   * weird jolt"). Three causes, three fixes:
   *
   * 1. `toggle` is queued, so closing the other row from it started that row's
   *    300ms collapse a frame or more after the clicked row began to grow. For
   *    that moment both bodies were open and the column was ~tens of px
   *    taller. The click handler closes the others BEFORE the browser opens
   *    the clicked row, in the same task, so one grows while the other shrinks
   *    with the same curve. Enter / Space on a <summary> dispatch `click` too.
   *    The `toggle` path below still covers anything else that opens a row.
   *
   * 2. Single-open rows still differ in height (643 / 646 / 654 measured), and
   *    the column sets the grid row, so the section changed height on every
   *    click. stabilise() measures the column in every single-open state with
   *    the transitions off and pins it at the tallest as a min-height. A
   *    closed-all state is shorter than that, so it is covered too.
   *
   * 3. Chrome's scroll anchoring picked the phone or a row as its anchor and
   *    scrolled the page to follow it; that is turned off for this body in
   *    sections/dev-feature-showcase.liquid (overflow-anchor: none).
   */
  document.addEventListener(
    'click',
    (event) => {
      if (event.defaultPrevented || !(event.target instanceof Element)) return;
      const summary = event.target.closest('summary');
      const item = summary?.parentElement;
      if (!item || !item.matches(ITEM) || summary !== item.querySelector(':scope > summary')) return;
      // Only when this click is about to OPEN the row.
      if (!item.open) closeOthers(item);
    },
    true
  );

  const MEASURING = 'data-dev-feature-measuring';
  /** Last inline size each group was measured at. */
  const measuredAt = new WeakMap();

  const stabilise = (group) => {
    const rows = [...group.querySelectorAll(ITEM)];
    if (rows.length < 2 || rows.some((row) => row.hasAttribute(MULTI))) return;

    const initial = rows.map((row) => row.open);
    group.setAttribute(MEASURING, '');
    group.style.minHeight = '';

    // -1 = every row closed, then each row open on its own. All synchronous,
    // so none of these states is ever painted.
    const summaryHeight = (row) => row.querySelector(':scope > summary')?.getBoundingClientRect().height ?? 0;
    let tallest = 0;
    let closedSummaries = [];
    let summaryGrowth = 0;
    for (let i = -1; i < rows.length; i += 1) {
      rows.forEach((row, j) => { row.open = j === i; });
      tallest = Math.max(tallest, group.getBoundingClientRect().height);
      if (i === -1) closedSummaries = rows.map(summaryHeight);
      else summaryGrowth = Math.max(summaryGrowth, summaryHeight(rows[i]) - closedSummaries[i]);
    }
    // An open title is set looser than a closed one (1.3 vs 1.1), and that
    // switch is instant while the bodies animate. Mid-swap the new row's taller
    // title can sit on top of the old row's still-open body, so the tallest
    // single state is short by up to one title's growth. Measured 7.4px at 1280.
    tallest += summaryGrowth;

    rows.forEach((row, j) => { row.open = initial[j]; });
    // Flush the restored state while transitions are still off, so turning
    // them back on does not animate from the last measured state.
    group.getBoundingClientRect();
    group.removeAttribute(MEASURING);

    group.style.minHeight = `${Math.ceil(tallest)}px`;
    measuredAt.set(group, group.getBoundingClientRect().width);
  };

  const groups = (root) => {
    const found = new Set();
    for (const item of root.querySelectorAll(ITEM)) {
      const group = groupOf(item);
      if (group) found.add(group);
    }
    return found;
  };

  // Heights change with the column width and the web font, so re-measure on
  // both. Deferred a frame: measuring inside the observer callback resizes the
  // observed element and trips the "ResizeObserver loop" warning.
  const observer = 'ResizeObserver' in window
    ? new ResizeObserver((entries) => {
        for (const { target } of entries) {
          if (Math.abs(target.getBoundingClientRect().width - (measuredAt.get(target) ?? -1)) < 0.5) continue;
          requestAnimationFrame(() => stabilise(target));
        }
      })
    : null;

  const setUp = (root) => {
    for (const group of groups(root)) {
      stabilise(group);
      observer?.observe(group);
    }
  };

  setUp(document);
  document.fonts?.ready.then(() => groups(document).forEach(stabilise));
  // Theme editor: a re-rendered section brings new rows (and a new group).
  document.addEventListener('shopify:section:load', (event) => setUp(event.target));

  document.addEventListener(
    'toggle',
    (event) => {
      const item = event.target;
      if (!(item instanceof Element) || !item.matches(ITEM)) return;

      // Mirrors `open` onto the summary and the root so CSS and assistive tech
      // agree with the element. Kept in sync here rather than only in Liquid,
      // because the browser flips `open` without telling the markup.
      item.classList.toggle('is-open', item.open);
      item.querySelector(':scope > summary')?.setAttribute('aria-expanded', item.open ? 'true' : 'false');

      if (item.open) closeOthers(item);
    },
    true
  );
}
